/**
 * Per-keyword × snapshot Local Pack heatmap. Each cell colour-codes the client's map-pack
 * position for that keyword on that snapshot date. Includes a smart "all off-pack" banner
 * that switches messaging based on whether the snapshot was taken from coordinates (hyper-
 * local) or from the town centroid.
 */
import React, { useMemo, useState } from "react";
import { MapPin, AlertCircle, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { fmtDateShort } from "../../../lib/format";

function packCellStyle(pos) {
  if (pos == null) return { bg: "#f7fafc", fg: "#cbd5e0", label: "·", title: "Not in Local Pack" };
  if (pos <= 3) return { bg: "#38b2ac", fg: "#ffffff", label: `#${pos}`, title: `Pack position ${pos}` };
  if (pos <= 10) return { bg: "#4fd1c5", fg: "#1a202c", label: `#${pos}`, title: `Pack position ${pos}` };
  if (pos <= 20) return { bg: "#f6ad55", fg: "#1a202c", label: `#${pos}`, title: `Pack position ${pos}` };
  return { bg: "#fc8181", fg: "#1a202c", label: `#${pos}`, title: `Beyond top 20 (#${pos})` };
}

function HeatmapLegend() {
  const items = [
    { color: "#38b2ac", label: "Top 3" },
    { color: "#4fd1c5", label: "4–10" },
    { color: "#f6ad55", label: "11–20" },
    { color: "#fc8181", label: "21+" },
    { color: "#f7fafc", label: "Off pack", border: "#e2e8f0" },
  ];
  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap text-[10px] font-mono-tight uppercase tracking-widest">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1">
          <span
            className="h-3 w-3 rounded-sm"
            style={{ background: it.color, border: it.border ? `1px solid ${it.border}` : undefined }}
          />
          <span className="text-[#718096]">{it.label}</span>
        </span>
      ))}
    </div>
  );
}

function SortIcon({ active, dir }) {
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <Icon
      className={`h-3 w-3 transition-opacity ${
        active ? "opacity-100 text-[#2c7a7b]" : "opacity-40 group-hover/sort:opacity-80"
      }`}
      strokeWidth={2.5}
    />
  );
}

export default function LocalPackHeatmap({ data, client }) {
  const snapshots = data?.snapshots || [];
  const dataKeywords = data?.keywords || [];

  // Merge the client's currently-tracked keywords with whatever appeared in past
  // snapshots, so a freshly-added keyword shows up immediately as "pending"
  // instead of being invisible until the next Local Finder run.
  const { keywords, pendingSet } = useMemo(() => {
    const tracked = client?.keywords || [];
    const inSnaps = new Set(dataKeywords);
    const merged = [];
    const pending = new Set();
    // Tracked-but-not-yet-crawled appear first (in the order the user added them),
    // so it's obvious what's still waiting on the next cron.
    for (const kw of tracked) {
      if (!inSnaps.has(kw)) {
        merged.push(kw);
        pending.add(kw);
      }
    }
    for (const kw of dataKeywords) merged.push(kw);
    return { keywords: merged, pendingSet: pending };
  }, [dataKeywords, client?.keywords]);

  const hasData = snapshots.length > 0 && keywords.length > 0;
  // Detect "all off-pack" — common when the geo-centroid radius excludes the client's actual address.
  // Pending keywords are excluded from this calculation because they haven't been crawled yet —
  // counting them would always trigger the "all off pack" banner for any client with new keywords.
  const crawledKeywords = keywords.filter((kw) => !pendingSet.has(kw));
  const crawledCellsTotal = snapshots.length * crawledKeywords.length;
  const filledCells = snapshots.reduce(
    (acc, s) => acc + Object.values(s.positions || {}).filter((v) => v != null).length,
    0,
  );
  const allOffPack = hasData && crawledCellsTotal > 0 && filledCells === 0;

  // Case-insensitive keyword filter — only renders matching rows.
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();
  const filteredKeywords = useMemo(() => {
    if (!trimmedQuery) return keywords;
    return keywords.filter((kw) => (kw || "").toLowerCase().includes(trimmedQuery));
  }, [keywords, trimmedQuery]);

  // Sort state — tri-state cycle (none → asc → desc → none) per column.
  // sortKey can be "keyword" (alphabetical) or a snapshot's `at` timestamp string
  // (sort by that column's position values). Pending rows always sit at the
  // bottom of an active sort because they have no data to rank against.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir(null);
    }
  };

  const displayedKeywords = useMemo(() => {
    if (!sortKey || !sortDir) return filteredKeywords;
    const snapByAt = new Map(snapshots.map((s) => [s.at, s]));
    const accessor = (kw) => {
      if (pendingSet.has(kw)) return null; // pending → bottom regardless of dir
      if (sortKey === "keyword") return (kw || "").toLowerCase();
      const snap = snapByAt.get(sortKey);
      const pos = snap?.positions?.[kw];
      return pos == null ? null : pos;
    };
    const decorated = filteredKeywords.map((kw, i) => ({ kw, i, v: accessor(kw) }));
    decorated.sort((a, b) => {
      const aNull = a.v == null;
      const bNull = b.v == null;
      if (aNull && bNull) return a.i - b.i;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof a.v === "string" && typeof b.v === "string") {
        const cmp = a.v.localeCompare(b.v);
        return sortDir === "asc" ? cmp : -cmp;
      }
      if (a.v === b.v) return a.i - b.i;
      return sortDir === "asc" ? a.v - b.v : b.v - a.v;
    });
    return decorated.map((d) => d.kw);
  }, [filteredKeywords, sortKey, sortDir, snapshots, pendingSet]);

  return (
    <div className="card-soft overflow-hidden" data-testid="local-pack-heatmap">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
            <MapPin className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Local Pack heatmap</div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Per-keyword map position over time</h3>
          </div>
        </div>
        <HeatmapLegend />
      </div>
      {!hasData ? (
        <div className="p-6 text-center text-xs text-[#a0aec0] font-mono-tight">
          No Local Finder snapshots yet. Click <span className="font-semibold text-[#1a202c]">Refresh Local Finder</span> above to seed the matrix.
        </div>
      ) : (
        <>
          {allOffPack && (
            <div className="mx-5 my-3 rounded-xl border border-[#feebc8] bg-[#fffaf0] p-3 text-xs flex items-start gap-2.5" data-testid="heatmap-off-pack-banner">
              <AlertCircle className="h-4 w-4 text-[#dd6b20] shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-[#7b341e]">Your business isn&apos;t in the local pack for any tracked keyword.</div>
                {snapshots[0]?.search_origin === "coordinate" ? (
                  <div className="text-[#9c4221] mt-0.5">
                    Hyper-local search is already active from your GBP coordinates within a {client.search_radius_km || 5} km radius — so this reflects what real users searching near your business see on Google. Focus on GBP optimisation: more reviews, fresh posts, fully-completed categories and services, geo-tagged photos.
                  </div>
                ) : (
                  <div className="text-[#9c4221] mt-0.5">
                    DataForSEO is using the geo-centroid of <span className="font-mono-tight">{snapshots[0]?.service_location || "your service location"}</span>. If your address is on the edge of town, your GBP won&apos;t appear here even when it does for real users near you. <span className="font-semibold">Fix:</span> paste a GBP CID on the Edit Client form and click <span className="font-semibold">Test</span> — coordinates are auto-captured and future runs will search from your actual location.
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="px-5 py-3 border-b border-[#edf2f7] flex items-center gap-3 flex-wrap" data-testid="local-heatmap-search-bar">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a0aec0] pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search local keywords…"
                aria-label="Search local pack keywords"
                data-testid="local-heatmap-search-input"
                className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-[#edf2f7] bg-white text-[#1a202c] placeholder:text-[#a0aec0] focus:outline-none focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/20 transition-colors"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  data-testid="local-heatmap-search-clear"
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full inline-flex items-center justify-center text-[#a0aec0] hover:text-[#4a5568] hover:bg-[#edf2f7] transition-colors"
                >
                  <X className="h-3 w-3" strokeWidth={2.4} />
                </button>
              )}
            </div>
            <div
              className="text-xs font-mono-tight text-[#a0aec0]"
              data-testid="local-heatmap-search-count"
            >
              {trimmedQuery ? (
                <span>
                  <span className="text-[#1a202c] font-semibold">{filteredKeywords.length}</span>
                  <span> of {keywords.length} keywords</span>
                </span>
              ) : (
                <span>{keywords.length} keywords tracked</span>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#edf2f7] bg-[#f7fafc]">
                  <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold sticky left-0 bg-[#f7fafc] z-10 min-w-[200px]">
                    <button
                      type="button"
                      onClick={() => handleSort("keyword")}
                      data-testid="local-heatmap-sort-keyword"
                      title="Sort keywords alphabetically"
                      className={`inline-flex items-center gap-1 hover:text-[#1a202c] transition-colors group/sort ${
                        sortKey === "keyword" ? "text-[#1a202c]" : ""
                      }`}
                    >
                      <span>Keyword</span>
                      <SortIcon active={sortKey === "keyword"} dir={sortDir} />
                    </button>
                  </th>
                  {snapshots.map((s, i) => {
                    const key = s.at || `snap-${i}`;
                    const active = sortKey === key;
                    return (
                      <th
                        key={key}
                        className="text-center px-2 py-2.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold min-w-[64px]"
                        title={s.at}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(key)}
                          data-testid={`local-heatmap-sort-snap-${i}`}
                          title={`Sort by position on ${fmtDateShort(s.at)}`}
                          className={`inline-flex items-center gap-1 hover:text-[#1a202c] transition-colors group/sort ${
                            active ? "text-[#1a202c]" : ""
                          }`}
                        >
                          <span>{fmtDateShort(s.at)}</span>
                          <SortIcon active={active} dir={sortDir} />
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayedKeywords.length === 0 ? (
                  <tr>
                    <td
                      colSpan={snapshots.length + 1}
                      className="px-5 py-6 text-center text-xs text-[#a0aec0]"
                      data-testid="local-heatmap-empty"
                    >
                      No keywords match &ldquo;{query}&rdquo;.
                    </td>
                  </tr>
                ) : (
                  displayedKeywords.map((kw) => {
                  const isPending = pendingSet.has(kw);
                  return (
                  <tr
                    key={kw}
                    data-testid={isPending ? `local-heatmap-row-pending-${kw}` : undefined}
                    className="border-b border-[#edf2f7] last:border-0 hover:bg-[#f7fafc]/40"
                  >
                    <td className="px-5 py-2 text-[#1a202c] sticky left-0 bg-white max-w-[280px]" title={kw}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate">{kw}</span>
                        {isPending && (
                          <span
                            data-testid={`local-heatmap-pending-${kw}`}
                            title="Added recently — will be crawled on the next Local Finder run"
                            className="shrink-0 text-[9px] uppercase tracking-widest font-bold text-[#744210] bg-[#fefcbf]/80 border border-[#f6e05e]/60 px-1.5 py-0.5 rounded-md"
                          >
                            Pending
                          </span>
                        )}
                      </div>
                    </td>
                    {snapshots.map((s, i) => {
                      if (isPending) {
                        return (
                          <td key={(s.at || i) + kw} className="text-center px-2 py-1.5" title="Awaiting first Local Finder crawl">
                            <span
                              className="inline-flex items-center justify-center rounded-md font-mono-tight font-bold h-7 min-w-[40px] px-1 border border-dashed border-[#e2e8f0] text-[#cbd5e0] bg-transparent"
                              data-testid={`heatmap-cell-${kw}-${i}`}
                            >
                              ·
                            </span>
                          </td>
                        );
                      }
                      const pos = s.positions?.[kw];
                      const cell = packCellStyle(pos);
                      return (
                        <td key={(s.at || i) + kw} className="text-center px-2 py-1.5" title={cell.title}>
                          <span
                            className="inline-flex items-center justify-center rounded-md font-mono-tight font-bold h-7 min-w-[40px] px-1"
                            style={{ background: cell.bg, color: cell.fg }}
                            data-testid={`heatmap-cell-${kw}-${i}`}
                          >
                            {cell.label}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
